import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { D2C_OPS_ORDERS_API_URL } from "../../Shared/constant";
import PostRequest from "../../../Api/PostRequest";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Callout } from "./ui/misc";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { cn } from "./ui/utils";
import StateBlock from "./StateBlock";
import useD2cGet, { errorMessage } from "./useD2cGet";
import { formatDateTime, formatKm, humaniseCode } from "./d2cFormat";

// Re-push a D2C order to one dealer (OMS ops/orders/<id>/repush). The OMS
// decides who can be picked and why not; this only shows its answer and sends
// the choice. Allowed only while the order is still with dealers.
const RepushDialog = ({ orderId, open, preselectStoreId, onClose, onDone, onRefused }) => {
  const { accessToken } = useSelector((state) => state.adminReducers);
  const url = open && orderId ? `${D2C_OPS_ORDERS_API_URL}/${encodeURIComponent(orderId)}/repush` : null;
  const { data, loading, error, reload } = useD2cGet(url);
  const [picked, setPicked] = useState(null);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    if (open) {
      setPicked(preselectStoreId || null);
      setFailure(null);
      setNotice(null);
    }
  }, [open, preselectStoreId]);

  const stores = data?.stores || [];
  const chosen = stores.find((s) => s.store_id === picked);
  // Not while the list reloads (after a refusal): the old rows may be stale.
  const canSend = Boolean(data?.allowed && chosen?.selectable) && !saving && !loading;
  // A dealer picked from an offer row who has since left D2C or the search area.
  const preselectMissing = Boolean(data?.allowed && preselectStoreId && !stores.some((s) => s.store_id === preselectStoreId));

  const send = async () => {
    setSaving(true);
    setFailure(null);
    setNotice(null);
    const response = await PostRequest(url, { store_id: picked }, accessToken, true);
    setSaving(false);
    if (response?.status === 200 && response?.data?.changed === false) {
      setNotice(`Already live for ${chosen?.franchise_code || "this dealer"}; nothing was sent.`);
    } else if (response?.status === 200) {
      onDone(response?.data);
    } else {
      // The order may have moved on (accepted, back at central): refresh both
      // this list and the drawer behind it.
      setFailure(errorMessage(response));
      reload();
      onRefused();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !saving && onClose()}>
      <DialogContent className="tw-max-w-2xl">
        <DialogHeader>
          <DialogTitle>Re-push to a dealer</DialogTitle>
          <DialogDescription>
            Enrolled dealers near the customer, nearest first. The order stays open for them for a while longer, and the
            first dealer to accept gets it.
          </DialogDescription>
        </DialogHeader>
        <StateBlock loading={loading} error={error} onRetry={reload} isEmpty={!data} emptyText="No answer from the OMS.">
          {data && !data.allowed && <Callout tone="warning">Cannot re-push: {data.reason}</Callout>}
          {data?.allowed && data.window_closes_at && (
            <div className="tw-text-xs tw-text-muted-foreground">Acceptance window closes {formatDateTime(data.window_closes_at)}</div>
          )}
          {preselectMissing && (
            <Callout tone="warning">That dealer is no longer enrolled for D2C or near enough to this customer.</Callout>
          )}
          {data?.allowed && stores.length === 0 && (
            <div className="tw-text-sm tw-text-muted-foreground">No enrolled dealer near the customer.</div>
          )}
          {stores.length > 0 && (
            // Native radios: one tab stop and arrow keys between enabled dealers.
            <fieldset className="tw-m-0 tw-max-h-80 tw-space-y-1 tw-overflow-y-auto tw-border-0 tw-p-0">
              <legend className="tw-sr-only">Dealers</legend>
              {stores.map((store) => {
                const selectable = Boolean(data.allowed && store.selectable);
                const active = store.store_id === picked;
                return (
                  <label
                    key={store.store_id}
                    className={cn(
                      "tw-m-0 tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-md tw-border tw-px-3 tw-py-2",
                      active && "tw-border-primary tw-bg-muted",
                      !selectable && "tw-cursor-not-allowed tw-opacity-60",
                    )}
                  >
                    <input
                      type="radio"
                      name="repush-dealer"
                      value={store.store_id}
                      checked={active}
                      disabled={!selectable}
                      onChange={() => setPicked(store.store_id)}
                      className="tw-shrink-0"
                    />
                    <span className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-justify-between tw-gap-3">
                      <span className="tw-min-w-0">
                        <span className="tw-block tw-font-medium">{store.name || store.franchise_code}</span>
                        <span className="tw-block tw-font-mono tw-text-[11px] tw-text-muted-foreground">{store.franchise_code}</span>
                        {store.why_not && <span className="tw-block tw-text-xs tw-text-muted-foreground">{store.why_not}</span>}
                      </span>
                      <span className="tw-flex tw-shrink-0 tw-items-center tw-gap-2 tw-text-xs tw-tabular-nums">
                        {store.offer_status && <Badge tone="neutral">{humaniseCode(store.offer_status)}</Badge>}
                        <span>{formatKm(store.distance_km)}</span>
                        <span>{store.free_units} free</span>
                      </span>
                    </span>
                  </label>
                );
              })}
            </fieldset>
          )}
        </StateBlock>
        {/* Outside StateBlock, so a reload that fails cannot hide why the send did. */}
        {failure && <Callout tone="danger">{failure}</Callout>}
        {notice && <Callout tone="info">{notice}</Callout>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={send} disabled={!canSend}>
            {saving ? "Re-pushing…" : chosen ? `Re-push to ${chosen.franchise_code}` : "Re-push"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RepushDialog;
